/**
 * Segmented range picker for the analysis page. Replaces the dropdown
 * menu in the legacy `templates/analysis.html` with a simpler segmented
 * control. The Custom option expands a small panel with two date inputs
 * and an Apply button — mirrors the `data-range-custom` form behavior.
 */

import { useState, type FormEvent } from "react";

import type { RangeKey } from "../../api/types";

interface Option {
  key: RangeKey;
  label: string;
}

const OPTIONS: Option[] = [
  { key: "month", label: "Current month" },
  { key: "last_month", label: "Last month" },
  { key: "ytd", label: "YTD" },
  { key: "last_12_months", label: "Last 12 months" },
  { key: "all_time", label: "All time" },
  { key: "custom", label: "Custom" },
];

export interface RangePickerProps {
  value: RangeKey;
  start?: string;
  end?: string;
  onChange: (range: RangeKey, start?: string, end?: string) => void;
}

export default function RangePicker({
  value,
  start,
  end,
  onChange,
}: RangePickerProps) {
  const [draftStart, setDraftStart] = useState(start ?? "");
  const [draftEnd, setDraftEnd] = useState(end ?? "");

  const onSelect = (key: RangeKey) => {
    if (key === "custom") {
      // Switch to custom but don't fire the query until Apply is clicked
      // with both dates populated.
      onChange("custom", draftStart || undefined, draftEnd || undefined);
      return;
    }
    onChange(key);
  };

  const onApply = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (draftStart && draftEnd) {
      onChange("custom", draftStart, draftEnd);
    }
  };

  return (
    <div className="range-picker" data-range-picker>
      <div role="group" aria-label="Date range">
        {OPTIONS.map((opt) => {
          const active = opt.key === value;
          return (
            <button
              key={opt.key}
              type="button"
              className={
                "range-picker__option" +
                (active ? " range-picker__option--active" : "")
              }
              aria-pressed={active}
              onClick={() => onSelect(opt.key)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {value === "custom" ? (
        <form className="range-picker__custom" onSubmit={onApply}>
          <label className="field">
            <span className="field__label">From</span>
            <input
              className="field__input"
              type="date"
              name="start"
              required
              value={draftStart}
              onChange={(e) => setDraftStart(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">To</span>
            <input
              className="field__input"
              type="date"
              name="end"
              required
              value={draftEnd}
              onChange={(e) => setDraftEnd(e.target.value)}
            />
          </label>
          <button
            className="button button--primary"
            type="submit"
            disabled={!draftStart || !draftEnd}
          >
            Apply
          </button>
        </form>
      ) : null}
    </div>
  );
}
